import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import { initUserName } from "../../../utils/shortenName";

const EmployeeOrdersByExpeditor = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const expeditorColors = [
    "#4E7CFF",
    "#22C0FF",
    "#FFB5CE",
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E9",
  ];

  const processData = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        processedData: [
          {
            clientId: "no-data",
            clientName: "Нет данных",
            shortName: "Нет данных",
          }
        ],
        expeditors: [],
        maxCount: 0
      };
    }

    const filteredData = data.filter(item => 
      item.expeditorName && item.expeditorName.trim()
    );

    if (filteredData.length === 0) {
      return {
        processedData: [
          {
            clientId: "no-data",
            clientName: "Нет данных",
            shortName: "Нет данных",
          }
        ],
        expeditors: [],
        maxCount: 0
      };
    }

    const groupedByClient = filteredData.reduce((acc, item) => {
      const clientKey = item.clientId;
      if (!acc[clientKey]) {
        acc[clientKey] = {
          clientId: item.clientId,
          clientName: item.clientName,
          expeditors: {}
        };
      }
      
      const expeditorName = item.expeditorName.trim();
      
      if (acc[clientKey].expeditors[expeditorName]) {
        acc[clientKey].expeditors[expeditorName] += item.count;
      } else {
        acc[clientKey].expeditors[expeditorName] = item.count;
      }
      
      return acc;
    }, {});

    const allExpeditors = [...new Set(filteredData.map(item => item.expeditorName.trim()))];
    
    const processedData = Object.values(groupedByClient).map(client => {
      const clientData = {
        clientId: client.clientId,
        clientName: client.clientName,
        shortName: initUserName(client.clientName),
        category: client.clientName,
      };
      
      allExpeditors.forEach(expeditor => {
        if (client.expeditors[expeditor]) {
          clientData[expeditor] = client.expeditors[expeditor];
        }
      });
      
      return clientData;
    });

    const maxCount = Math.max(...processedData.map(client => {
      return allExpeditors.reduce((sum, expeditor) => {
        return sum + (client[expeditor] || 0);
      }, 0);
    }));

    return {
      processedData,
      expeditors: allExpeditors,
      maxCount
    };
  };

  const { processedData, expeditors, maxCount } = useMemo(() => {
    return processData(item);
  }, [item]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-employee-orders-expeditor");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelX: "zoomY",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "shortName",
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 50,
          inversed: true,
        }),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{clientName}"
        }),
      }),
    );
    yAxis.data.setAll(processedData);

    let xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: Math.ceil(maxCount * 1.1),
        renderer: am5xy.AxisRendererX.new(root, {}),
      }),
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomY",
        yAxis: yAxis,
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      visible: true,
    });

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      visible: true,
    });

    expeditors.forEach((expeditor, index) => {
      const color = expeditorColors[index % expeditorColors.length];
      
      let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: expeditor,
          xAxis: xAxis,
          yAxis: yAxis,
          categoryYField: "shortName",
          valueXField: expeditor,
          stacked: true,
        }),
      );

      series.columns.template.setAll({
        fill: am5.color(color),
        stroke: am5.color(color),
        strokeWidth: 1,
        maxHeight: 80
      });

      series.set(
        "tooltip",
        am5.Tooltip.new(root, {
          labelText: `${expeditor}: {valueX}`,
        }),
      );

      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationX: 0.5,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            text: "{valueX}",
            fill: am5.color("#FFFFFF"),
            fontSize: 11,
            centerX: am5.p50,
            centerY: am5.p50,
            populateText: true,
            visible: true,
          }),
        });
      });

      series.data.setAll(processedData);
    });

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, expeditors, maxCount, currentLanguage]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>
          {t("analytics.employeeRequestDistributionByCarrier")}
        </span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        {expeditors.map((expeditor, index) => (
          <div key={expeditor} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div 
              className={style.area_icon} 
              style={{ backgroundColor: expeditorColors[index % expeditorColors.length] }}
            ></div>
            <div className={style.area_type}>{expeditor}</div>
          </div>
        ))}
      </div>

      <div className={style.area}>
        <div id="chartdiv-employee-orders-expeditor" className={style.area_chart}></div>
        
        <div className={style.area_names}>
          {expeditors.map((expeditor, index) => (
            <div key={expeditor} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <div 
                className={style.area_icon} 
                style={{ backgroundColor: expeditorColors[index % expeditorColors.length] }}
              ></div>
              <div className={style.area_type}>{expeditor}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmployeeOrdersByExpeditor;